import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateFieldError } from '@/lib/i18n/translateFieldError';
import Select, { components, type OptionProps, type SingleValueProps, type StylesConfig } from 'react-select';
import i18n from '@/lib/i18n/i18n';
import ReactCountryFlag from 'react-country-flag';
import { getCountryCallingCode, getCountries } from 'libphonenumber-js';

interface CountryOption {
    value: string;
    label: string;
    phoneCode: string;
    searchText: string;
}

interface PhoneInputProps {
    name: string;
    placeholder: string;
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    type: string;
    error?: string;
}

function getCountryDisplayName(countryCode: string, locale: string): string {
    try {
        return (
            new Intl.DisplayNames([locale.startsWith('ar') ? 'ar' : 'en'], { type: 'region' }).of(countryCode) ??
            countryCode
        );
    } catch {
        return countryCode;
    }
}

function Flag({ countryCode }: { countryCode: string }) {
    return (
        <ReactCountryFlag
            countryCode={countryCode}
            svg
            style={{
                width: 20,
                height: 20,
                borderRadius: 2,
                flexShrink: 0,
                display: 'block',
            }}
        />
    );
}

function CountrySingleValue(props: SingleValueProps<CountryOption, false>) {
    const { data } = props;
    return (
        <components.SingleValue {...props}>
            <div className="flex items-center gap-1.5">
                <Flag countryCode={data.value} />
                <span className="text-sm font-medium tabular-nums">{data.phoneCode}</span>
            </div>
        </components.SingleValue>
    );
}

function CountryOption(props: OptionProps<CountryOption, false>) {
    const { data } = props;
    return (
        <components.Option {...props}>
            <div className="flex w-full items-center gap-2.5">
                <Flag countryCode={data.value} />
                <span className="min-w-0 flex-1 truncate text-sm">{data.label}</span>
                <span className="shrink-0 text-sm tabular-nums text-gray-500 dark:text-gray-400">{data.phoneCode}</span>
            </div>
        </components.Option>
    );
}

function getMenuWidth() {
    if (typeof window === 'undefined') return 280;
    return Math.min(280, window.innerWidth - 24);
}

const PhoneInput: React.FC<PhoneInputProps> = ({ name, placeholder, label, value, onChange, type, error }) => {
    const { t } = useTranslation();
    const errorMessage = translateFieldError(error, name, t);
    const inputId = `${name}-${React.useId()}`;
    const isRtl = i18n.dir() === 'rtl';
    const locale = i18n.language;

    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    const countries: CountryOption[] = useMemo(
        () =>
            getCountries()
                .map((country) => {
                    const phoneCode = `+${getCountryCallingCode(country)}`;
                    const countryName = getCountryDisplayName(country, locale);
                    return {
                        value: country,
                        label: countryName,
                        phoneCode,
                        searchText: `${countryName} ${country} ${phoneCode}`.toLowerCase(),
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label, locale.startsWith('ar') ? 'ar' : 'en')),
        [locale],
    );

    const [selectedCountry, setSelectedCountry] = useState<CountryOption>(
        () => countries.find((c) => c.value === 'EG') || countries[0],
    );

    useEffect(() => {
        const next =
            countries.find((c) => c.value === selectedCountry.value) ||
            countries.find((c) => c.value === 'EG') ||
            countries[0];
        setSelectedCountry(next);
    }, [countries, selectedCountry.value]);

    const handleCountryChange = (selectedOption: CountryOption | null) => {
        if (!selectedOption) return;

        setSelectedCountry(selectedOption);

        const nationalNumber = value.replace(/^\+\d*/, '');
        onChange({
            target: {
                name,
                value: `${selectedOption.phoneCode}${nationalNumber}`,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value;

        if (!inputValue.startsWith(selectedCountry.phoneCode)) {
            inputValue = `${selectedCountry.phoneCode}${inputValue.replace(/^\+\d*/, '')}`;
        }

        onChange({
            target: {
                name,
                value: inputValue,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    const selectStyles: StylesConfig<CountryOption, false> = {
        control: (base, state) => ({
            ...base,
            minHeight: 48,
            height: 48,
            minWidth: 108,
            borderRadius: isRtl ? '0.5rem 0 0 0.5rem' : '0 0.5rem 0.5rem 0',
            borderTopLeftRadius: isRtl ? '0.5rem' : 0,
            borderBottomLeftRadius: isRtl ? '0.5rem' : 0,
            borderTopRightRadius: isRtl ? 0 : '0.5rem',
            borderBottomRightRadius: isRtl ? 0 : '0.5rem',
            borderColor: state.isFocused ? '#3B82F6' : error ? '#EF4444' : isDark ? '#262626' : '#D1D5DB',
            borderLeftWidth: isRtl ? 1 : 0,
            borderRightWidth: isRtl ? 0 : 1,
            backgroundColor: isDark ? '#0D0D0D' : '#FFFFFF',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                borderColor: state.isFocused ? '#3B82F6' : isDark ? '#404040' : '#9CA3AF',
            },
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 4px 0 10px',
        }),
        singleValue: (base) => ({
            ...base,
            margin: 0,
            maxWidth: '100%',
            color: isDark ? '#FFFFFF' : '#111827',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: 48,
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 6px',
            color: isDark ? '#9CA3AF' : '#6B7280',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        menu: (base) => {
            const menuWidth = getMenuWidth();
            return {
                ...base,
                width: menuWidth,
                minWidth: menuWidth,
                marginTop: 6,
                borderRadius: 10,
                overflow: 'hidden',
                backgroundColor: isDark ? '#0D0D0D' : '#FFFFFF',
                border: isDark ? '1px solid #262626' : '1px solid #E5E7EB',
                boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.45)' : '0 12px 28px rgba(15, 23, 42, 0.12)',
            };
        },
        menuPortal: (base) => {
            const menuWidth = getMenuWidth();
            const rawLeft = typeof base.left === 'number' ? base.left : Number.parseFloat(String(base.left ?? 0)) || 0;
            const left = Math.max(12, Math.min(rawLeft, window.innerWidth - menuWidth - 12));

            return {
                ...base,
                left,
                zIndex: 10000,
                width: menuWidth,
            };
        },
        menuList: (base) => ({
            ...base,
            maxHeight: 260,
            padding: 6,
        }),
        option: (base, state) => ({
            ...base,
            padding: '10px 12px',
            borderRadius: 8,
            cursor: 'pointer',
            backgroundColor: state.isSelected
                ? '#1e63a3'
                : state.isFocused
                  ? isDark
                      ? '#1F1F1F'
                      : '#F3F4F6'
                  : 'transparent',
            color: state.isSelected ? '#FFFFFF' : isDark ? '#FFFFFF' : '#111827',
            '&:active': {
                backgroundColor: '#1e63a3',
                color: '#FFFFFF',
            },
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
            color: isDark ? '#FFFFFF' : '#111827',
        }),
        placeholder: (base) => ({
            ...base,
            color: isDark ? '#9CA3AF' : '#6B7280',
        }),
    };

    return (
        <div className="flex flex-col gap-2">
            <label htmlFor={inputId} className="text-base font-medium text-primery-blue-900 dark:text-white">
                {label}
            </label>

            <div className="flex w-full flex-row-reverse items-stretch" >
                <Select<CountryOption, false>
                    options={countries}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    className="shrink-0"
                    classNamePrefix="phone-country"
                    inputId={`${inputId}-country`}
                    aria-label={t('signUp.branchCreate.country')}
                    isSearchable
                    components={{
                        SingleValue: CountrySingleValue,
                        Option: CountryOption,
                    }}
                    filterOption={(option, rawInput) =>
                        option.data.searchText.includes(rawInput.trim().toLowerCase())
                    }
                    styles={selectStyles}
                    menuPlacement="auto"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                />

                <input
                    id={inputId}
                    type={type}
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={handlePhoneChange}
                    className={`h-12 w-full flex-1 border px-3 text-base transition-colors focus:outline-none focus:ring-2
                        bg-white text-gray-900 placeholder-gray-400
                        dark:bg-[#0D0D0D] dark:text-[#86A4C0] dark:placeholder-gray-500
                        ${isRtl ? 'rounded-r-lg' : 'rounded-l-lg'}
                        ${
                            error
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200 dark:border-[#262626]'
                        }`}
                    
                />
            </div>

            {errorMessage ? <p className="mt-1 text-sm text-red-600">{errorMessage}</p> : null}
        </div>
    );
};

export default PhoneInput;
